import { supabase } from '../config/supabase';

export interface DashboardStats {
  total_users: number;
  active_users_today: number;
  premium_users: number;
  new_users_this_month: number;
  total_profiles_saved: number;
  total_labels_created: number;
  posts_blocked_today: number;
  system_health: 'excellent' | 'good' | 'warning' | 'critical';
}

export interface SystemStats {
  total_users: number;
  active_users_30d: number;
  total_profiles: number;
  total_companies: number;
  total_reviews: number;
  total_labels: number;
  profiles_with_notes: number;
  system_health: string;
}

/**
 * Serviço para buscar dados reais do dashboard do Supabase
 */
class DashboardService {
  /**
   * Busca estatísticas gerais do sistema usando a função get_system_stats
   */
  async getSystemStats(): Promise<SystemStats | null> {
    try {
      const { data, error } = await supabase.rpc('get_system_stats');

      if (error) {
        console.error('Erro ao buscar estatísticas do sistema:', error);
        return null;
      }

      return data as SystemStats;
    } catch (error) {
      console.error('Erro na comunicação com Supabase:', error);
      return null;
    }
  }

  /**
   * Busca estatísticas dos usuários usando a view extension_user_stats
   */
  async getUserStats(): Promise<{
    total: number;
    activeToday: number;
    active30d: number;
    newThisMonth: number;
  }> {
    try {
      // Buscar total de usuários
      const { count: totalUsers } = await supabase
        .from('extension_user_stats')
        .select('*', { count: 'exact', head: true });

      // Buscar usuários ativos hoje
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { count: activeToday } = await supabase
        .from('extension_user_stats')
        .select('*', { count: 'exact', head: true })
        .gte('last_activity', today.toISOString());

      // Buscar usuários ativos nos últimos 30 dias
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { count: active30d } = await supabase
        .from('extension_user_stats')
        .select('*', { count: 'exact', head: true })
        .eq('activity_status', 'active');

      // Buscar novos usuários deste mês
      const firstDayOfMonth = new Date();
      firstDayOfMonth.setDate(1);
      firstDayOfMonth.setHours(0, 0, 0, 0);
      
      const { count: newThisMonth } = await supabase
        .from('extension_user_stats')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', firstDayOfMonth.toISOString());

      return {
        total: totalUsers || 0,
        activeToday: activeToday || 0,
        active30d: active30d || 0,
        newThisMonth: newThisMonth || 0,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas de usuários:', error);
      return {
        total: 0,
        activeToday: 0,
        active30d: 0,
        newThisMonth: 0,
      };
    }
  }

  /**
   * Busca estatísticas de perfis usando a view linkedin_profile_stats
   */
  async getProfileStats(): Promise<{
    total: number;
    savedByUsers: number;
  }> {
    try {
      const { count: total } = await supabase
        .from('linkedin_profile_stats')
        .select('*', { count: 'exact', head: true });

      // Soma de todos os perfis salvos
      const { data: profiles } = await supabase
        .from('linkedin_profile_stats')
        .select('saved_by_users');

      const savedByUsers = profiles?.reduce((sum, profile) => sum + (profile.saved_by_users || 0), 0) || 0;

      return {
        total: total || 0,
        savedByUsers,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas de perfis:', error);
      return {
        total: 0,
        savedByUsers: 0,
      };
    }
  }

  /**
   * Busca estatísticas de labels usando a view label_usage_stats
   */
  async getLabelStats(): Promise<{
    total: number;
    profilesCount: number;
  }> {
    try {
      const { count: total } = await supabase
        .from('label_usage_stats')
        .select('*', { count: 'exact', head: true });

      // Soma de todos os perfis que usam labels
      const { data: labels } = await supabase
        .from('label_usage_stats')
        .select('profiles_count');

      const profilesCount = labels?.reduce((sum, label) => sum + (label.profiles_count || 0), 0) || 0;

      return {
        total: total || 0,
        profilesCount,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas de labels:', error);
      return {
        total: 0,
        profilesCount: 0,
      };
    }
  }

  /**
   * Busca estatísticas de empresas usando a view company_stats
   */
  async getCompanyStats(): Promise<{
    total: number;
    totalReviews: number;
  }> {
    try {
      const { count: total } = await supabase
        .from('company_stats')
        .select('*', { count: 'exact', head: true });

      // Soma de todos os reviews
      const { data: companies } = await supabase
        .from('company_stats')
        .select('total_reviews');

      const totalReviews = companies?.reduce((sum, company) => sum + (company.total_reviews || 0), 0) || 0;

      return {
        total: total || 0,
        totalReviews,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas de empresas:', error);
      return {
        total: 0,
        totalReviews: 0,
      };
    }
  }

  /**
   * Busca estatísticas completas do dashboard
   */
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      // Buscar todas as estatísticas em paralelo
      const [systemStats, userStats, profileStats, labelStats] = await Promise.all([
        this.getSystemStats(),
        this.getUserStats(),
        this.getProfileStats(),
        this.getLabelStats(),
      ]);

      // Calcular premium users (usuários com mais de 10 perfis salvos)
      // Por enquanto, vamos usar uma estimativa baseada em usuários ativos
      const premiumUsers = Math.floor((userStats.active30d || 0) * 0.22); // ~22% dos usuários ativos

      // Posts bloqueados hoje (placeholder - precisa de tabela específica)
      const postsBlockedToday = 0;

      // Determinar saúde do sistema
      let systemHealth: 'excellent' | 'good' | 'warning' | 'critical' = 'excellent';
      if (systemStats?.system_health) {
        systemHealth = systemStats.system_health as 'excellent' | 'good' | 'warning' | 'critical';
      }

      return {
        total_users: userStats.total || systemStats?.total_users || 0,
        active_users_today: userStats.activeToday || 0,
        premium_users: premiumUsers,
        new_users_this_month: userStats.newThisMonth || 0,
        total_profiles_saved: profileStats.savedByUsers || systemStats?.total_profiles || 0,
        total_labels_created: labelStats.total || systemStats?.total_labels || 0,
        posts_blocked_today: postsBlockedToday,
        system_health: systemHealth,
      };
    } catch (error) {
      console.error('Erro ao buscar estatísticas do dashboard:', error);
      // Retornar dados padrão em caso de erro
      return {
        total_users: 0,
        active_users_today: 0,
        premium_users: 0,
        new_users_this_month: 0,
        total_profiles_saved: 0,
        total_labels_created: 0,
        posts_blocked_today: 0,
        system_health: 'warning',
      };
    }
  }

  /**
   * Busca atividades recentes dos logs administrativos
   */
  async getRecentActivity(limit: number = 10): Promise<any[]> {
    try {
      const { data, error } = await supabase
        .from('admin_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Erro ao buscar atividades recentes:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Erro ao buscar atividades recentes:', error);
      return [];
    }
  }
}

export default new DashboardService();

